/**
 * テキストの行数を自動制限する機能
 *
 * 仕様
 * - ist-cardMiddle__contentInner: 親要素
 * - ist-cardMiddle__titleText: タイトル
 * - ist-cardMiddle__mainText: 本文
 * - ist-cardMiddle__labels: ラベルリスト
 * - ist-cardMiddle__label: ラベル
 *
 * 使い方
 * <div class="ist-cardMiddle__contentInner">
 *   <div class="ist-cardMiddle__titleText">タイトル</div>
 *   <div class="ist-cardMiddle__mainText">本文</div>
 *   <ul class="ist-cardMiddle__labels">
 *     <li class="ist-cardMiddle__label">ラベル</li>
 *     <li class="ist-cardMiddle__label">ラベル</li>
 *     <li class="ist-cardMiddle__label">ラベル</li>
 *     <li class="ist-cardMiddle__label">ラベル</li>
 *     <li class="ist-cardMiddle__label">ラベル</li>
 *   </ul>
 * </div>
 */

export class CardMiddleLineClamp {
  // クラス名の定数
  private static readonly CLASSES = {
    CONTENT_INNER: 'ist-cardMiddle__contentInner',
    TITLE_TEXT: 'ist-cardMiddle__titleText',
    MAIN_TEXT: 'ist-cardMiddle__mainText',
    LABELS: 'ist-cardMiddle__labels',
    LABEL: 'ist-cardMiddle__label',
  };

  // リサイズ監視用のオブザーバー
  private resizeObserver: ResizeObserver;

  // リサイズ処理のタイムアウトID
  private resizeTimeout: number | null = null;

  // 非表示用のクラス名
  private readonly NO_DISPLAY_CLASS = 'ist-js__cardMiddleTextNodisplay';

  // デバッグモードのフラグ
  private debug: boolean;

  /**
   * コンストラクタ
   * @param options デバッグモードを有効にするオプション
   */
  constructor(options: { debug?: boolean } = {}) {
    this.debug = options.debug || false;
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  /**
   * 初期化処理
   * 対象要素の監視を開始し、行数制限を適用する
   */
  private initialize(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(CardMiddleLineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resizeObserver.observe(contentInner);
      // ラベルリストにoverflow: hiddenを適用
      const labelsContainer = contentInner.querySelector(
        `.${CardMiddleLineClamp.CLASSES.LABELS}`
      ) as HTMLElement;
      if (labelsContainer) {
        labelsContainer.style.overflow = 'hidden';
      }
    });

    this.updateAllLineClamps();
  }

  /**
   * 要素の実際の高さを取得する
   * @param element 測定対象の要素
   * @returns 要素の高さ(px)
   */
  private getActualHeight(element: HTMLElement): number {
    const clone = element.cloneNode(true) as HTMLElement;
    const parent = element.parentElement;

    if (!parent) return 0;

    clone.style.cssText = `
      position: absolute !important;
      visibility: hidden !important;
      display: block !important;
      width: ${element.offsetWidth}px !important;
      height: auto !important;
      margin: 0 !important;
      padding: ${window.getComputedStyle(element).padding} !important;
    `;

    parent.appendChild(clone);
    const height = clone.scrollHeight;
    parent.removeChild(clone);

    this.log('Actual height measurement:', {
      element: element.className,
      height,
      content: element.textContent?.slice(0, 50) + '...',
    });

    return height;
  }

  /**
   * 要素の行の高さを取得する
   * @param element 測定対象の要素
   * @returns 行の高さ(px)
   */
  private getLineHeight(element: HTMLElement): number {
    const style = window.getComputedStyle(element);
    let lineHeight = parseFloat(style.lineHeight);

    if (isNaN(lineHeight)) {
      const fontSize = parseFloat(style.fontSize);
      lineHeight = fontSize * 1.2;
    }

    this.log('Line height:', lineHeight);
    return lineHeight;
  }

  /**
   * 要素のスタイルをリセットする
   * @param contentInner リセット対象の親要素
   */
  private resetStyles(contentInner: HTMLElement): void {
    const titleText = contentInner.querySelector(
      `.${CardMiddleLineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    const mainText = contentInner.querySelector(
      `.${CardMiddleLineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    const labels = contentInner.querySelectorAll(
      `.${CardMiddleLineClamp.CLASSES.LABEL}`
    ) as NodeListOf<HTMLElement>;

    if (titleText) {
      titleText.style.removeProperty('--line-clamp');
      titleText.classList.remove(this.NO_DISPLAY_CLASS);
    }
    if (mainText) {
      mainText.style.removeProperty('--line-clamp');
      mainText.classList.remove(this.NO_DISPLAY_CLASS);
    }
    labels.forEach((label) => {
      label.classList.remove(this.NO_DISPLAY_CLASS);
      label.style.display = ''; // displayプロパティもリセット
    });
  }

  /**
   * 要素の行数制限を計算して適用する
   * @param contentInner 計算対象の親要素
   */
  private calculateLineClamp(contentInner: HTMLElement): void {
    // コンテンツ全体の高さ
    const contentInnerHeight = contentInner.offsetHeight;
    this.log('Content inner height:', contentInnerHeight);

    // 各要素の取得
    const titleText = contentInner.querySelector(
      `.${CardMiddleLineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    const mainText = contentInner.querySelector(
      `.${CardMiddleLineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    const labels = Array.from(
      contentInner.querySelectorAll(`.${CardMiddleLineClamp.CLASSES.LABEL}`)
    ) as HTMLElement[];

    // タイトルが存在しない場合は処理を終了
    if (!titleText) return;

    // タイトルの行高さと実際の高さを取得
    const titleTextLineHeight = this.getLineHeight(titleText);
    const actualTitleHeight = this.getActualHeight(titleText);

    // タイトルのmargin-topを取得
    const titleStyle = window.getComputedStyle(titleText);
    const titleMarginTop = parseFloat(titleStyle.marginTop);
    const titleMarginBottom = parseFloat(titleStyle.marginBottom);

    // 本文の行高さを取得
    const mainTextLineHeight = this.getLineHeight(mainText);

    // 本文のマージンとパディングを取得
    const mainStyle = window.getComputedStyle(mainText);
    const mainMarginTop = parseFloat(mainStyle.marginTop);
    const mainPaddingTop = parseFloat(mainStyle.paddingTop);
    const mainMarginBottom = parseFloat(mainStyle.marginBottom);

    // 本文の実際の高さを取得
    const actualMainHeight = this.getActualHeight(mainText);

    // 利用可能な高さを計算（ラベル用を後で計算）
    const availableHeightForMainAndLabels =
      contentInnerHeight -
      titleMarginTop -
      actualTitleHeight -
      titleMarginBottom -
      mainMarginTop -
      mainPaddingTop -
      mainMarginBottom;

    this.log('Space calculation:', {
      contentInnerHeight,
      titleMarginTop,
      actualTitleHeight,
      titleMarginBottom,
      mainMarginTop,
      mainPaddingTop,
      mainMarginBottom,
      availableHeightForMainAndLabels,
    });

    // タイトルの表示制御
    if (actualTitleHeight > titleTextLineHeight) {
      const titleMaxLines = Math.floor(actualTitleHeight / titleTextLineHeight);
      if (titleMaxLines <= 0) {
        titleText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        titleText.style.setProperty('--line-clamp', titleMaxLines.toString());
      }
    }

    // 本文の表示制御
    if (actualMainHeight > availableHeightForMainAndLabels) {
      const mainMaxLines = Math.floor(
        availableHeightForMainAndLabels / mainTextLineHeight
      );
      if (mainMaxLines <= 0) {
        mainText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        mainText.style.setProperty('--line-clamp', mainMaxLines.toString());
        mainText.classList.remove(this.NO_DISPLAY_CLASS);
      }
    } else {
      mainText.classList.remove(this.NO_DISPLAY_CLASS);
    }

    // ラベルの表示制御
    const labelsContainer = contentInner.querySelector(
      `.${CardMiddleLineClamp.CLASSES.LABELS}`
    ) as HTMLElement;
    if (!labelsContainer) return;

    // ラベルの合計高さとマージンを計算
    let labelsTotalHeight = 0;
    let labelsTotalMarginTop = 0;

    labels.forEach((label) => {
      const labelLineHeight = this.getLineHeight(label);
      const actualLabelHeight = this.getActualHeight(label);
      const labelStyle = window.getComputedStyle(label);
      const labelMarginTop = parseFloat(labelStyle.marginTop);
      labelsTotalHeight += actualLabelHeight;
      labelsTotalMarginTop += labelMarginTop;
    });

    // 本文とラベルに利用可能な高さを計算
    const availableHeightForLabels =
      availableHeightForMainAndLabels -
      (this.getActualHeight(mainText) > availableHeightForMainAndLabels
        ? Math.floor(availableHeightForMainAndLabels / mainTextLineHeight) *
          mainTextLineHeight
        : this.getActualHeight(mainText));

    this.log('Available height for labels:', availableHeightForLabels);

    if (availableHeightForLabels <= 0) {
      labels.forEach((label) => label.classList.add(this.NO_DISPLAY_CLASS));
      return;
    }

    // ラベルを順番に表示し、利用可能な高さ内に収まるかチェック
    let accumulatedHeight = 0;
    labels.forEach((label) => {
      const labelStyle = window.getComputedStyle(label);
      const labelMarginTop = parseFloat(labelStyle.marginTop);
      const labelHeight = this.getActualHeight(label);

      if (
        accumulatedHeight + labelMarginTop + labelHeight <=
        availableHeightForLabels
      ) {
        accumulatedHeight += labelMarginTop + labelHeight;
        label.classList.remove(this.NO_DISPLAY_CLASS);
      } else {
        label.classList.add(this.NO_DISPLAY_CLASS);
      }
    });
  }

  /**
   * リサイズイベントのハンドラー
   * @param entries リサイズ対象の要素リスト
   */
  private handleResize(entries: ResizeObserverEntry[]): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }

    this.resizeTimeout = window.requestAnimationFrame(() => {
      entries.forEach((entry) => {
        const contentInner = entry.target as HTMLElement;
        this.resetStyles(contentInner);
        this.calculateLineClamp(contentInner);
      });
      this.resizeTimeout = null;
    });
  }

  /**
   * 全ての要素の行数制限を更新する
   */
  private updateAllLineClamps(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(CardMiddleLineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resetStyles(contentInner);
      this.calculateLineClamp(contentInner);
    });
  }

  /**
   * デバッグログを出力する
   * @param args ログ出力する引数
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[LineClamp]', ...args);
    }
  }

  /**
   * インスタンスを破棄する
   */
  public destroy(): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
    this.resizeObserver.disconnect();
  }

  /**
   * 行数制限を再計算する
   */
  public recalculate(): void {
    this.updateAllLineClamps();
  }
}
